"use client";
import { useEffect, useState } from "react";
import { Box, Stack, Typography, Container } from "@mui/material";
import ModelsIcon from "@mui-symbols-material/w400/DeployedCodeFilledRounded";

import { useStore } from "../../../../../service/mobx";
import ButtonFilled from "../../../../../component/Button/Filled";
import ButtonOutlined from "../../../../../component/Button/Outlined";
import Widgets from "./Widgets";
// import ButtonText from "../../../../../component/Button/Text";

const Api = ({ count, models }) => (
  <Container
    maxWidth="large"
    sx={{
      gap: 4,
      display: "flex",
      flexDirection: "column",
      pt: { compact: 8, expanded: 5 }
    }}
  >
    <ProductLine />
    <Headline count={count} />
    <Grant />
    <Widgets count={count} models={models} />
  </Container>
);

export default Api;

const ProductLine = () => (
  <Stack alignItems="center">
    <ModelsIcon
      sx={{
        color: "var(--primary-color)",
        width: { compact: 64, expanded: 80 },
        height: { compact: 64, expanded: 80 }
      }}
    />
    <Typography
      fontWeight="700 !important"
      color="var(--secondary-color)"
      typography={{ compact: "headlineSm", expanded: "displayMd" }}
    >
      Model API
    </Typography>
    <Typography
      fontWeight="500 !important"
      color="var(--secondary-color)"
      typography={{ compact: "titleMd", expanded: "titleLg" }}
    >
      Largest inference API on the internet
    </Typography>
  </Stack>
);

function Headline({ count }) {
  const { utilities } = useStore();
  const buttonStyle = {
    borderRadius: "var(--shape-sm)",
    fontSize: "16px !important"
  };
  const [returnTo, setReturnTo] = useState("");

  // this component is rendered server side for some reason
  // window is not available on the server, so this allows us to only render this part on the client
  useEffect(() => {
    setReturnTo(encodeURIComponent(window.location.href));
  }, []);

  useEffect(() => {
    const models = [
      "Run GPT 4.5",
      "Run DeepSeek",
      "Run Gemini Pro",
      "Run open + closed models",
      `Run ${count.models}+ models`
    ];

    let currentIndex = 0;
    let characterIndex = 0;
    let isDeleting = false;
    const typingDelay = 100;
    const erasingDelay = 50;
    const newModelDelay = 2000;

    function typeWriter() {
      const currentModel = models[currentIndex];
      const textElement = document.querySelector(".animated-text");

      if (!textElement) {
        return;
      }

      // Last model becomes permanent
      if (
        currentIndex === models.length - 1 &&
        characterIndex === currentModel.length
      ) {
        return;
      }

      if (isDeleting) {
        // Erasing text
        textElement.textContent = currentModel.substring(0, characterIndex - 1);
        characterIndex--;

        if (characterIndex === 0) {
          isDeleting = false;
          currentIndex = (currentIndex + 1) % models.length;
          timeoutId = setTimeout(typeWriter, typingDelay);
        } else {
          timeoutId = setTimeout(typeWriter, erasingDelay);
        }
      } else {
        // Typing text
        textElement.textContent = currentModel.substring(0, characterIndex + 1);
        characterIndex++;

        if (characterIndex === currentModel.length) {
          // If we're at the last model, don't delete it
          if (currentIndex === models.length - 1) {
            return textElement.classList.remove("animated-text");
          }

          isDeleting = true;
          timeoutId = setTimeout(typeWriter, newModelDelay);
        } else {
          timeoutId = setTimeout(typeWriter, typingDelay);
        }
      }
    }

    // Start the typing animation after a slight delay
    let timeoutId = setTimeout(typeWriter, 2000);

    return () => clearTimeout(timeoutId);
  }, [utilities, count]);

  return (
    <Stack alignItems="center">
      <Box
        overflow="hidden"
        sx={{
          "@keyframes blink": {
            "0%": { opacity: 0 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0 }
          },
          "& .animated-text::after": {
            content: '"|"',
            color: "var(--secondary-color)",
            animation: "blink 1s infinite"
          }
        }}
      >
        <Typography
          align="center"
          className="animated-text"
          sx={{ textWrap: "balance" }}
          fontWeight="700 !important"
          typography={{ compact: "displaySm", expanded: "displayLg" }}
        />
      </Box>
      <Typography
        component="h3"
        fontWeight="600 !important"
        typography={{ compact: "headlineMd", expanded: "displaySm" }}
      >
        with one API key
      </Typography>
      <Stack direction="row" justifyContent="space-between" spacing={2} mt={4}>
        <ButtonFilled
          label="Get free key"
          href={returnTo ? `/api?returnTo=${returnTo}` : "/api"}
          sx={buttonStyle}
        />
        <ButtonOutlined
          sx={buttonStyle}
          label="Explore models"
          href="/models"
        />
        {/* <ButtonText
          sx={buttonStyle}
          target="_blank"
          label="Docs"
          href="https://docs.bytez.com"
        /> */}
      </Stack>
    </Stack>
  );
}

export const Grant = ({ hideOnMobile }) => (
  <Container
    maxWidth="expanded"
    sx={{
      display: hideOnMobile ? { compact: "none", expanded: "block" } : "block"
    }}
  >
    <Box
      sx={{
        position: "relative",
        zIndex: 0, // create stacking context
        overflow: "hidden",
        borderRadius: "var(--shape-lg-round)",
        p: { compact: 2, expanded: 2 },

        // Base background
        background:
          "linear-gradient(135deg, var(--dark-surface-container-lowest) 62%, var(--dark-surface-container-highest) 100%)",

        boxShadow: "0 4px 16px rgba(124, 77, 255, 0.08)",
        transition: "all 500ms var(--motion-easing-emphasized)",

        // Hover layer (behind content)
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          background:
            "linear-gradient(135deg, var(--dark-surface-container-lowest) 38%, var(--dark-primary-container) 100%)",
          opacity: 0,
          transition: "opacity 500ms var(--motion-easing-emphasized)",
          zIndex: 0,
          pointerEvents: "none"
        },

        // Keep children above overlay
        "& > *": {
          position: "relative",
          zIndex: 1
        },

        "&:hover": {
          boxShadow: "0 6px 24px rgba(124, 77, 255, 0.12)"
        },
        "&:hover::before": { opacity: 1 }
      }}
    >
      <Stack
        direction={{ compact: "column", expanded: "row" }}
        alignItems={{ compact: "stretch", expanded: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        {/* Left side - Text content */}
        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
          {/* Badge Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              minWidth: 64,
              background:
                "linear-gradient(135deg, var(--dark-surface-container-highest) 0%, var(--dark-surface-dim) 100%)",
              borderRadius: "12px",
              display: { compact: "none", expanded: "flex" },
              alignItems: "center",
              justifyContent: "center",
              fontSize: 32
            }}
          >
            💰
          </Box>

          {/* Text Content */}
          <Stack width="100%">
            <Typography
              textAlign={{ compact: "center", expanded: "left" }}
              mb={0.5}
              typography={{ compact: "titleMd", expanded: "titleLg" }}
              sx={{
                fontSize: {
                  compact: "18px !important",
                  expanded: "22px !important"
                },
                fontWeight: "700 !important",
                color: "#fff"
              }}
            >
              AI Grant: $200k for Startups
            </Typography>

            <Typography
              component="p"
              color="var(--dark-secondary-color)"
              typography="bodyMd"
              sx={{ textWrap: "balance" }}
              display={{ compact: "none", expanded: "block" }}
            >
              We're giving startups free credits to run any AI model — from
              Claude 4.5 to the latest open-source.
            </Typography>

            <Typography
              component="p"
              align="center"
              color="var(--dark-secondary-color)"
              typography="bodyMd"
              sx={{ textWrap: "balance" }}
              display={{ compact: "block", expanded: "none" }}
            >
              Free credits to run open + closed models
            </Typography>
          </Stack>
        </Stack>

        {/* Right side - CTA and logos */}
        <Stack
          direction="column"
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <Stack direction="row" spacing={2} alignItems="center" width="100%">
            {/* Logos - Minimal version */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { compact: "none", expanded: "flex" } }}
            >
              <GrantLogo bgcolor="#f0652f" text="YC" color="#fff" />
              <GrantLogo bgcolor="#000" text="TS" color="#39C463" />
              <GrantLogo bgcolor="#fff" text="500" color="#000" />
            </Stack>

            {/* CTA Button */}
            <ButtonFilled
              fullWidth
              size="medium"
              label="Apply For Credits →"
              href="https://docs.google.com/forms/d/e/1FAIpQLSfpm9hHTKRLTBrudOnikqM47etOhIhXiTbf0bBeFbhpqw9VZg/viewform?usp=pp_url&entry.956726539=Bytez.com"
              target="_blank"
              sx={{
                px: 3,
                py: 1,
                fontSize: "14px !important",
                fontWeight: 600,
                borderRadius: "8px",
                background:
                  "linear-gradient(135deg, var(--secondary-color) 0%, var(--primary-color) 100%)",
                whiteSpace: "nowrap",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color) 100%)",
                  boxShadow: "0 2px 8px rgba(124, 77, 255, 0.25)"
                }
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  </Container>
);

const GrantLogo = ({ text, color, bgcolor }) => (
  <Box
    sx={{
      height: 36,
      width: 36,
      display: "flex",
      bgcolor,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "var(--shape-sm)"
    }}
  >
    <Typography variant="labelMd" fontWeight={700} color={color}>
      {text}
    </Typography>
  </Box>
);
